import React, { useState, useContext, useEffect, forwardRef, useCallback } from 'react';
import Dialog from '@mui/material/Dialog';
import DialogTitle from '@mui/material/DialogTitle';
import DialogContent from '@mui/material/DialogContent';
import DialogActions from '@mui/material/DialogActions';
import IconButton from '@mui/material/IconButton';
import CloseIcon from '@mui/icons-material/Close';
import Typography from '@mui/material/Typography';
import Button from '@mui/material/Button';
import Box from '@mui/material/Box';
import Slide from '@mui/material/Slide';
import { useTranslation } from 'react-i18next';
import ItemIconV3 from './ItemIconV3';
import { AuthContext } from '../../contexts/AuthContext';
import { useSnackbar } from '../../contexts/SnackbarContext';
import { 
    fetchLoadAmmo,
    fetchUnloadAmmo,
    withAuthCheck,
    AuthError
} from '../../services/api';
import { v3Theme } from '../theme';

const Transition = forwardRef(function Transition(props, ref) {
    return <Slide direction="up" ref={ref} {...props} />;
});

export default function AmmoLoadUnloadDialog({ open, onClose, item, action }) {
    const token = useContext(AuthContext);
    const { showSnackbar } = useSnackbar();
    const { t } = useTranslation();
    const [isProcessing, setIsProcessing] = useState(false);
    
    const [ammoQty, setAmmoQty] = useState(1);

    // Reset quantity when dialog opens or action/item changes
    useEffect(() => {
        if (open && item) {
            let newQty;
            if (action === 'load') {
                // For load: use maxLoadable (how much can be loaded)
                const maxLoadable = Number(item.maxLoadable || 0);
                newQty = maxLoadable > 0 ? maxLoadable : 1;
            } else {
                // For unload: use quantity (how much is currently loaded)
                const qty = Number(item.quantity || item.qty || 1);
                newQty = qty;
            }
            setAmmoQty(newQty);
        }
    }, [open, action, item]);

    // For load action: use maxLoadable to limit the maximum
    // For unload action: use quantity (how much is currently loaded)
    const maxQty = item 
        ? (action === 'load' 
            ? Number(item.maxLoadable || 0) 
            : Number(item.quantity || item.qty || 1)) 
        : 1;

    const handleIncrement = useCallback(() => {
        setAmmoQty(prev => Math.min(prev + 1, maxQty));
    }, [maxQty]);

    const handleDecrement = useCallback(() => {
        setAmmoQty(prev => Math.max(prev - 1, 1));
    }, []);

    const handleInputChange = useCallback((e) => {
        const value = e.target.value;
        if (value === '') {
            setAmmoQty('');
            return;
        }
        const num = parseInt(value, 10);
        if (!isNaN(num) && num >= 1 && num <= maxQty) {
            setAmmoQty(num);
        }
    }, [maxQty]);

    const handleInputBlur = useCallback(() => {
        if (ammoQty === '' || ammoQty < 1) {
            setAmmoQty(1);
        } else if (ammoQty > maxQty) {
            setAmmoQty(maxQty);
        }
    }, [ammoQty, maxQty]);

    const handleSubmit = () => {
        if (!item || !item.code || !ammoQty || ammoQty <= 0) return;
        setIsProcessing(true);
        
        const apiCall = action === 'load' ? fetchLoadAmmo : fetchUnloadAmmo;
        
        withAuthCheck(apiCall, token, item.code, Number(ammoQty))
            .then(data => {
                setIsProcessing(false);
                onClose();
                if (data.success) {
                    showSnackbar(data.result, 'success');
                } else {
                    showSnackbar(data.error || data.result || 'Operation failed', 'error');
                }
            })
            .catch(err => {
                setIsProcessing(false);
                onClose();
                if (err instanceof AuthError) return;
                const errorMessage = err.result || err.message || 'error';
                showSnackbar(errorMessage, 'error');
            });
    };

    if (!item) return null;

    return (
        <Dialog 
            open={open} 
            onClose={onClose} 
            maxWidth="xs" 
            fullWidth
            TransitionComponent={Transition}
            PaperProps={{
                sx: {
                    borderRadius: 2,
                }
            }}
        >
            <DialogTitle
                sx={{ 
                    display: 'flex', 
                    alignItems: 'center', 
                    justifyContent: 'space-between',
                    pb: 2
                }}
            >
                <Typography variant="h6" component="div">
                    {t('dialogs.ammo.title', 'Ammo')}
                </Typography>
                <IconButton 
                    onClick={onClose}
                    size="small"
                    sx={{ mr: -1 }}
                >
                    <CloseIcon />
                </IconButton>
            </DialogTitle>
            <DialogContent sx={{ px: 3, pt: 2, pb: 2 }}>
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, mb: 3 }}>
                    <Box sx={{ 
                        width: 48, 
                        height: 48,
                        borderRadius: 1.5,
                        overflow: 'hidden',
                        flexShrink: 0
                    }}>
                        <ItemIconV3 
                            icon={item.icon} 
                            size={48}
                        />
                    </Box>
                    <Typography variant="body1">
                        {item.name}
                    </Typography>
                </Box>
                
                {/* Show warning if cannot load (maxLoadable is 0 for load action) */}
                {action === 'load' && item && item.maxLoadable === 0 && (
                    <Typography 
                        variant="body2" 
                        color="error" 
                        sx={{ textAlign: 'center', mb: 2 }}
                    >
                        {t('dialogs.ammo.cannotLoad', 'Cannot load: rack is full or ammo incompatible')}
                    </Typography>
                )}
                
                <Box sx={{ 
                    display: 'flex', 
                    alignItems: 'center', 
                    justifyContent: 'center',
                    gap: 2,
                    pt: 2,
                    pb: 1
                }}>
                    <Button
                        variant="outlined"
                        onClick={handleDecrement}
                        disabled={isProcessing || ammoQty <= 1}
                        sx={{
                            minWidth: 48,
                            height: 48,
                            borderRadius: 1.5,
                            fontSize: '20px',
                            fontWeight: 'bold',
                            borderColor: v3Theme.border.light,
                            color: v3Theme.main,
                            backgroundColor: 'transparent',
                            '&:hover': {
                                borderColor: v3Theme.main,
                                backgroundColor: v3Theme.background.hover,
                            },
                            '&:focus': {
                                outline: 'none',
                                borderColor: v3Theme.main,
                            },
                            '&.Mui-disabled': {
                                borderColor: v3Theme.border.light,
                                color: v3Theme.text.disabled,
                                backgroundColor: 'transparent',
                            }
                        }}
                    >
                        −
                    </Button>
                    <input
                        type="number"
                        value={ammoQty}
                        onChange={handleInputChange}
                        onBlur={handleInputBlur}
                        disabled={isProcessing}
                        style={{
                            width: '80px',
                            height: '48px',
                            fontSize: '18px',
                            fontWeight: 'bold',
                            textAlign: 'center',
                            border: `2px solid ${v3Theme.border.light}`,
                            borderRadius: '8px',
                            outline: 'none',
                            fontFamily: 'JetBrains Mono, monospace',
                            color: v3Theme.text.primary,
                            backgroundColor: v3Theme.background.paper,
                        }}
                        min={1}
                        max={maxQty}
                    />
                    <Button
                        variant="outlined"
                        onClick={handleIncrement}
                        disabled={isProcessing || ammoQty >= maxQty}
                        sx={{
                            minWidth: 48,
                            height: 48,
                            borderRadius: 1.5,
                            fontSize: '20px',
                            fontWeight: 'bold',
                            borderColor: v3Theme.border.light,
                            color: v3Theme.main,
                            backgroundColor: 'transparent',
                            '&:hover': {
                                borderColor: v3Theme.main,
                                backgroundColor: v3Theme.background.hover,
                            },
                            '&:focus': {
                                outline: 'none',
                                borderColor: v3Theme.main,
                            },
                            '&.Mui-disabled': {
                                borderColor: v3Theme.border.light,
                                color: v3Theme.text.disabled,
                                backgroundColor: 'transparent',
                            }
                        }}
                    >
                        +
                    </Button>
                </Box>
            </DialogContent>
            <DialogActions sx={{ justifyContent: 'center', pb: 3 }}>
                <Button
                    onClick={handleSubmit}
                    disabled={
                        isProcessing ||
                        ammoQty === '' ||
                        Number(ammoQty) <= 0 ||
                        Number(ammoQty) > maxQty ||
                        (action === 'load' && item && item.maxLoadable === 0)
                    }
                    variant="contained"
                    size="large"
                    sx={{
                        backgroundColor: v3Theme.main,
                        color: v3Theme.text.contrast,
                        border: 'none',
                        '&:hover': {
                            backgroundColor: v3Theme.background.dark,
                            border: 'none',
                        },
                        '&:focus': {
                            outline: 'none',
                            backgroundColor: v3Theme.main,
                        },
                        '&.Mui-disabled': {
                            backgroundColor: v3Theme.background.hover,
                            color: v3Theme.text.disabled,
                        },
                        textTransform: 'uppercase',
                        px: 5,
                        py: 1.5,
                        fontSize: '0.95rem',
                        fontWeight: 600,
                    }}
                >
                    {action === 'load' 
                        ? t('dialogs.itemActions.load', 'Load')
                        : t('dialogs.itemActions.unload', 'Unload')
                    }
                </Button>
            </DialogActions>
        </Dialog>
    );
}